import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Calendar, MapPin, Users, ChevronRight, Star } from 'lucide-react';
import { tournamentService } from '../services/tournamentService';
import type { Tournament } from '@/core/types/tournament';

const TournamentsListPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'official' | 'private'>('all');

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      const data = await tournamentService.getActiveTournaments(50);
      setTournaments(data);
    } catch (error) {
      console.error('Erro ao carregar torneios:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTournaments = tournaments.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-10 h-10" />
            <h1 className="text-3xl font-bold">Torneios de Pesca</h1>
          </div>
          <p className="text-amber-100 text-lg">
            Encontre campeonatos oficiais e torneios amadores em todo o Brasil
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-amber-500 text-black'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter('official')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
              filter === 'official'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <Star className="w-4 h-4" />
            Oficiais CBPE
          </button>
          <button
            onClick={() => setFilter('private')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'private'
                ? 'bg-green-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Amadores
          </button>
        </div>

        {/* Tournament Grid */}
        {filteredTournaments.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Nenhum torneio encontrado
            </h3>
            <p className="text-gray-500">
              {filter === 'all'
                ? 'Não há torneios ativos no momento.'
                : `Não há torneios ${filter === 'official' ? 'oficiais' : 'amadores'} ativos.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTournaments.map((tournament) => (
              <TournamentCard key={tournament.id} tournament={tournament} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TournamentCard: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  return (
    <Link
      to={`/torneios/${tournament.id}`}
      className="group bg-gray-800 rounded-xl overflow-hidden hover:bg-gray-750 transition-all hover:shadow-lg hover:shadow-amber-500/10"
    >
      {/* Banner */}
      <div className="relative h-40 bg-gradient-to-br from-gray-700 to-gray-800">
        {tournament.bannerUrl ? (
          <img
            src={tournament.bannerUrl}
            alt={tournament.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Trophy className="w-16 h-16 text-gray-600" />
          </div>
        )}
        
        {/* Badge Oficial */}
        {tournament.type === 'official' && (
          <div className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3" />
            OFICIAL CBPE
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
          {tournament.name}
        </h3>
        
        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Temporada {tournament.season}</span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className={`text-xs font-medium px-2 py-1 rounded ${
            tournament.isActive
              ? 'bg-green-500/20 text-green-400'
              : 'bg-gray-700 text-gray-400'
          }`}>
            {tournament.isActive ? 'Inscrições Abertas' : 'Encerrado'}
          </span>
          <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-amber-400 transition-colors" />
        </div>
      </div>
    </Link>
  );
};

export default TournamentsListPage;
